<template>
  <div>
    <div class="base-valid-range-number">
      <div class="prefix">
        <span>{{ fromPrefix }}</span>
      </div>
      <el-popover
          ref="elPopover"
          :popper-class="msgPopperClass"
          trigger="focus">
        <el-input
            ref="inputFrom"
            slot="reference"
            v-model="inputValue[0]"
            v-validate="validationRule"
            :data-vv-name="name"
            :data-vv-scope="validationScope"
            :class="{'is-error': $errorMsg }"
            :name="name"
            :disabled="disabled"
            :size="size"
            :label="label"
            :placeholder="placeholder"
            type="number"
            @change="onChange($event)"
            @focus="onFocus($event)"
            @blur="onBlur($event)" />
      </el-popover>
      <div class="suffix">
        <span>{{ fromSuffix }}</span>
      </div>
      <div class="toLabel">
        ~
      </div>
      <div class="prefix">
        <span>{{ toPrefix }}</span>
      </div>
      <el-popover
          ref="elPopover"
          :popper-class="msgPopperClass"
          trigger="focus">
        <el-input
            ref="inputTo"
            slot="reference"
            v-model="inputValue[1]"
            v-validate="validationRule"
            :data-vv-name="name"
            :data-vv-scope="validationScope"
            :class="{'is-error': $errorMsg }"
            :name="name"
            :disabled="disabled"
            :size="size"
            :label="label"
            :placeholder="placeholder"
            type="number"
            @change="onChange($event)"
            @focus="onFocus($event)"
            @blur="onBlur($event)" />
      </el-popover>
      <div class="suffix">
        <span>{{ toSuffix }}</span>
      </div>
    </div>
    <div v-show="$errorMsg"
        class="is-error-msg">
      {{ $errorMsg }}
    </div>
  </div>
</template>

<style scoped>
.base-valid-range-number ::v-deep input[type=number]::-webkit-inner-spin-button,
.base-valid-range-number ::v-deep input[type=number]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.base-valid-range-number {
  display: flex;
  align-items: center;
}
.base-valid-range-number .el-input {
  width: auto;
}
.base-valid-range-number ::v-deep input[type="number"] {
  -moz-appearance: textfield;
  text-align: right;
}
.base-valid-range-number ::v-deep input[type="number"]::placeholder{
  text-align: center;
}
.base-valid-range-number .toLabel {
  margin-left: 10px;
  margin-right: 10px;
}
.base-valid-range-number .prefix {
  margin-right: 5px;
}
.base-valid-range-number .suffix {
  margin-left: 5px;
}

.base-valid-range-number {
    font-size: 14px;
    font-weight: bold;
    color: #9da6ba;
}
</style>

<script>
import _ from 'lodash'
import ValidCore from '@/components/atoms/BaseValidCore'
import math from '@/utils/math'

const customValidationRules = (v) => {
  // 大小チェック
  v.$validator.extend('isRelationship' + v.name, {
    getMessage: () => '値の大小関係が不正です。',
    validate: (data) => {
      // from,toともに値が入っている。
      if (v.inputValue[0] && v.inputValue[1] &&
          v.toHalfWidthNumber(v.inputValue[0]) > v.toHalfWidthNumber(v.inputValue[1])) {
        return false
      }
      return true
    }
  })
}

export default {
  inject: ['parentValidator'],
  name: 'BaseValidRangeNumber',
  mixins: [ValidCore],
  props: {
    disabled: { type: Boolean, default: false },
    size: {
      type: String,
      default: undefined,
      validator: function (value) {
        return ['medium', 'small', 'mini'].indexOf(value) !== -1
      }
    },
    label: { type: String, default: undefined },
    placeholder: { type: String, default: undefined },
    precision: {
      type: Number,
      default: 0,
      validator: function validator (val) {
        return val >= 0 && val === parseInt(val, 10)
      }
    },
    maxLength: { type: Number, default: null },
    maxValue: { type: Number, default: null },
    minValue: { type: Number, default: null },
    fromPrefix: { type: String, default: '' },
    fromSuffix: { type: String, default: '' },
    toPrefix: { type: String, default: '' },
    toSuffix: { type: String, default: '' }
  },
  computed: {
    validationRule () {
      return `${this.precision > 0
        ? 'decimal:' + this.precision : ''}` +
        (this.minValue != null ? '|min_value:' + this.minValue : '') +
        (this.maxValue != null ? '|max_value:' + this.maxValue : '') +
        (this.maxLength != null ? '|max:' + this.maxLength : '') +
        '|isRelationship' + this.name
    },
    validationScope () {
      return _.get(this.$attrs, 'data-vv-scope', 'BaseValidRangeNumber')
    },
    $errorMsg () {
      if (this.errors.has(this.name, this.validationScope)) {
        return this.errors.first(this.name, this.validationScope)
      }
      return ''
    }
  },
  created () {
    // カスタムバリデーションルールを追加
    this.$validator = this.parentValidator
    customValidationRules(this)
  },
  mounted () {
    this.$el.addEventListener('paste', this.onPaste)
    this.$validator.validate(`${this.validationScope}.${this.name}`)
    /**
     * el-popoverに"tabindex"が設定されていると、
     * 中にあるinput要素でshift+tabキーが動作しないため"tabindex"を削除
     * 複数あり継承元のBaseValidCoreで対応できないため追加対応
     */
    this.$refs.elPopover.referenceElm.setAttribute('tabindex', -1)
  },
  beforeMount () {
    if (this.value === '') {
      this.inputValue = [ '', '' ]
    } else {
      this.inputValue = this.value
    }
  },
  beforeDestroy () {
    this.$el.removeEventListener('paste', this.onPaste)
  },
  methods: {
    onChange () {
      let updateValueFrom = this.toHalfWidthNumber(this.inputValue[0])
      // 空の場合は、不要な検索条件としてnullとする
      if (updateValueFrom !== null && this.inputValue[0] === '') {
        updateValueFrom = null
      }
      let updateValueTo = this.toHalfWidthNumber(this.inputValue[1])
      // 空の場合は、不要な検索条件としてnullとする
      if (updateValueTo !== null && this.inputValue[1] === '') {
        updateValueTo = null
      }
      this.updateInputValue([ updateValueFrom, updateValueTo ])
    },
    onFocus (event) {
      if (this.value === '') {
        this.inputValue = [ '', '' ]
      } else {
        this.inputValue = this.value
      }
      this.whenFocused = _.cloneDeep(this.inputValue)
      this.$emit('focus', event)
    },
    onBlur (event) {
      setTimeout(() => {
        if (this.$validator.fields.find({ name: this.name, scope: this.validationScope })) {
          this.$validator.validate(`${this.validationScope}.${this.name}`)
        }
      }, 100)

      this.$emit('blur', event, this.whenFocused, this.inputValue)
    },
    /**
     * valを半角数字の数値に変換し、カンマを削除して返却する.
     * valに数字以外が含まれている場合は、valをそのまま返却する.
     */
    toHalfWidthNumber (val) {
      if (typeof val === 'string' && val.match(/^(?:(?:[-－][０-９\d])|[０-９\d])(?:[.,．，]?[０-９\d]+)*$/) != null) {
        let ret = val
          .replace(/[０-９－．，]/g, char => String.fromCharCode(char.charCodeAt(0) - 0xFEE0))
          .replace(/,/g, '')
        ret = math.floor(
          Number(ret),
          this.precision)
        return Number.isNaN(ret) ? val : ret
      } else {
        return val
      }
    },
    // 親からインプット要素をフォーカスするために使われる
    focus: function (isFromFocus = true) {
      if (isFromFocus) {
        this.$refs.inputFrom.focus()
      } else {
        this.$refs.inputTo.focus()
      }
    }
  }
}
</script>
